// src/pages/expenses/ExpenseApproval.js
// v2 — 27 May 2026
// Governed: company_id filter, financial_transactions with full fields
// v3 — Costing Card Part A: project_id/project_name now passed through from
// the source site_purchase_requests doc (added by SitePurchase.js v3) into
// the financial_transactions entry, so expense cost attributes correctly.

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, updateDoc,
  doc, addDoc, query, where,
  orderBy, serverTimestamp
} from "firebase/firestore";

const CATEGORY_LABELS = {
  travel:  "Travel / Fuel",
  food:    "Food / Meals",
  labour:  "Labour / Helper",
  misc:    "Miscellaneous",
  expense: "Expense",
  local_purchase: "Local Purchase",
};

const EXPENSE_TYPE_LABELS = {
  already_spent: "Already Spent (Reimbursement)",
  need_advance:  "Need Advance",
};

export default function ExpenseApproval() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [data,          setData]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [processingId,  setProcessingId]  = useState(null); // item-level lock

  // Reject modal
  const [rejectModal,  setRejectModal]  = useState(null); // item.id
  const [rejectReason, setRejectReason] = useState("");

  // ── LOAD ─────────────────────────────────────────────────────────────
  const loadData = async () => {
    setLoading(true);
    try {
      // No orderBy — avoids composite index requirement
      const q = query(
        collection(db, "site_purchase_requests"),
        where("company_id", "==", companyId),
        where("status",     "==", "pending")
      );
      const snap = await getDocs(q);
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Filter expense type only — exclude old procurement docs
      const expenses = all
        .filter(d => d.type === "expense")
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));

      setData(expenses);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ── APPROVE ──────────────────────────────────────────────────────────
  const handleApprove = async (item) => {
    if (processingId === item.id) return; // double-click guard
    try {
      setProcessingId(item.id);

      const amount = Number(item.amount || 0);

      // Idempotency check — agar pehle se financial entry exist kare toh skip
      const dupCheck = await getDocs(query(
        collection(db, "financial_transactions"),
        where("company_id",    "==", companyId),
        where("reference_id",  "==", item.id),
        where("reference_type","==", "expense")
      ));
      if (!dupCheck.empty) {
        // Entry already exists — just update status if still pending
        await updateDoc(doc(db, "site_purchase_requests", item.id), {
          status:           "approved",
          approved_by:      user.uid || user.id || "",
          approved_by_name: user.name || "",
          approved_at:      serverTimestamp(),
          updated_at:       serverTimestamp(),
        });
        loadData();
        return;
      }

      // 1. Update expense status → approved
      await updateDoc(doc(db, "site_purchase_requests", item.id), {
        status:           "approved",
        approved_by:      user.uid || user.id || "",
        approved_by_name: user.name || "",
        approved_at:      serverTimestamp(),
        updated_at:       serverTimestamp(),
      });

      // 2. Create financial_transactions entry
      // Status "payment_pending" — accounts will pay and mark "paid"
      await addDoc(collection(db, "financial_transactions"), {
        company_id:       companyId,
        type:             "expense",
        category:         item.category || "misc",
        amount,
        description:      item.description || "",
        expense_type:     item.expense_type || "",
        reference_type:   "expense",
        reference_id:     item.id,
        requested_by:     item.requested_by || "",
        requested_by_name: item.requested_by_name || "",
        site_id:          item.site_id || "",
        site_name:        item.site_name || "",
        project_id:       item.project_id || "",
        project_name:     item.project_name || "",
        bill_url:         item.bill_url || "",
        approved_by:      user.uid || user.id || "",
        approved_by_name: user.name || "",
        status:           "payment_pending",  // Accounts will mark as paid
        created_at:       serverTimestamp(),
        updated_at:       serverTimestamp(),
      });

      loadData();
    } catch (err) {
      console.error(err);
      alert(err.message || "Approve failed");
    } finally {
      setProcessingId(null);
    }
  };

  // ── REJECT ───────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!rejectReason.trim()) { alert("Reason required"); return; }
    try {
      setLoading(true);
      await updateDoc(doc(db, "site_purchase_requests", rejectModal), {
        status:           "rejected",
        rejected_by:      user.uid || user.id || "",
        rejected_by_name: user.name || "",
        rejection_reason: rejectReason.trim(),
        rejected_at:      serverTimestamp(),
        updated_at:       serverTimestamp(),
      });
      setRejectModal(null);
      setRejectReason("");
      loadData();
    } catch (err) {
      alert(err.message || "Reject failed");
    } finally {
      setLoading(false);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen max-w-2xl mx-auto">

      <h1 className="text-xl font-bold text-slate-800 mb-5">Expense Approval</h1>

      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : data.length === 0 ? (
        <div className="text-center mt-16">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-slate-500 font-medium">No pending expenses</p>
          <p className="text-slate-400 text-sm mt-1">All caught up</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.map(item => (
            <div key={item.id}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
              style={{ borderLeft: "4px solid #D97706" }}>

              {/* HEADER */}
              <div className="p-4 pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-slate-800 text-base">
                      {CATEGORY_LABELS[item.category] || item.category || "Expense"}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      👤 {item.requested_by_name || "Unknown"}
                    </p>
                  </div>
                  <p className="text-xl font-black text-slate-800">
                    ₹{Number(item.amount || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* DETAILS */}
              <div className="px-4 pb-3 space-y-1">
                <p className="text-sm text-slate-600">{item.description}</p>

                <div className="flex gap-3 flex-wrap mt-1">
                  {item.expense_type && (
                    <span className={`text-xs font-semibold ${item.expense_type === "need_advance" ? "text-amber-600" : "text-slate-400"}`}>
                      {item.expense_type === "need_advance" ? "💵 Need Advance" : "🧾 Already Spent"}
                    </span>
                  )}
                  {item.site_name && (
                    <span className="text-xs text-blue-500">📍 {item.site_name}</span>
                  )}
                  {item.project_name && (
                    <span className="text-xs text-purple-500">🏗 {item.project_name}</span>
                  )}
                  {item.bill_url && (
                    <a href={item.bill_url} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-500 underline">
                      📄 View Bill
                    </a>
                  )}
                </div>
              </div>

              {/* ACTIONS */}
              <div className="flex gap-2 px-4 pb-4">
                <button onClick={() => handleApprove(item)}
                  disabled={processingId === item.id}
                  className="flex-1 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-40">
                  {processingId === item.id ? "Approving..." : "✓ Approve"}
                </button>
                <button onClick={() => { setRejectModal(item.id); setRejectReason(""); }}
                  disabled={processingId === item.id}
                  className="flex-1 py-2.5 rounded-xl bg-red-100 text-red-600 text-sm font-semibold disabled:opacity-40">
                  ✕ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5">
            <h3 className="font-bold text-slate-800 mb-1">Reject Expense</h3>
            <p className="text-xs text-slate-500 mb-3">
              Reason is mandatory. Tech will be notified.
            </p>
            <textarea rows={3} placeholder="Rejection reason *"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
              className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none mb-3" />
            <div className="flex gap-3">
              <button onClick={() => setRejectModal(null)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={handleReject}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold text-sm">
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
